/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-return -- type-aware lint resolves QuerySessionResponse/StreamingStatus as error via circular imports */
// eslint-disable-next-line @heidi/rules/no-new-legacy-session-data -- pre-existing recording hook importing only the StreamingStatus vocabulary from /contracts (the rule anchors on the first import, hence the placement)
import { activeRecordingTransportAtom } from '@/atoms/chronicle';
import {
  closeNativeBackupUploadWatch,
  openNativeBackupUploadWatch,
  raiseNativeBackupUploadGate,
} from '@/atoms/nativeBackupUploadGate';
import {
  preStartSystemMutePopoverOpenAtom,
  preStartSystemMuteUnhealthyAtom,
} from '@/atoms/preStartSystemMuteWarningAtom';
import {
  backupUploadPromptAtom,
  isCaptureTruncatedPromptFor,
  nativeBackupUploadGateAtom,
  requireBackupUploadModalAtom,
} from '@/atoms/sessionAtoms';
import {
  IS_LIVE_TRANSCRIPTS_ENABLED,
  IS_RELEASE_FORM_FILLING_FLOW_ALIGNMENT_ENABLED,
  isGenerateConsultNoteFromDocumentTemplateEnabled,
  useIsOfflineRecordingEnabled,
} from '@/features/flags';
import { getDesktopRecorderStartTimeoutMs } from '@/features/flags/desktop-recorder-start-timeout';
import { isDesktopRecordingLivenessEnabled } from '@/features/flags/desktop-recording-liveness-flag';
import { isDesktopSilentMicHardStopEnabled } from '@/features/flags/desktop-silent-mic-hard-stop-flag';
import { isDetachedBackupMuxEnabled } from '@/features/flags/detached-backup-mux-flag';
import {
  getFailedChunkThreshold,
  isSelfHealingStopEnabled,
} from '@/features/flags/self-healing-stop-flag';
import { isUserStartFenceKilled } from '@/features/flags/user-start-fence-kill-flag';
import { waitUntil } from '@/helpers/waitUntil';
import { isCaptureTruncatedVsWallClock } from '@/hooks/desktop/isCaptureTruncatedVsWallClock';
import { useCurrentSessionIsTabSharing } from '@/hooks/useCurrentSessionIsTabSharing';
import { sessionStopRecording } from '@/lib/analytics/scribe/session';
import {
  trackTauriStopFailedSurfaced,
  trackTauriStopRecordingPreventedAsRecordingStillInitializing,
} from '@/lib/analytics/scribe/tauri';
import { stopOfflineRecordingAudio } from '@/lib/pwa/recorder';
import { getUniqueId } from '@/lib/pwa/utils';
import {
  DesktopCaptureError,
  DesktopCaptureFailure,
} from '@/lib/telemetry/desktopCaptureErrors';
import {
  finishRecordingControlAttempt,
  localCaptureTransport,
  setRecordingControlTransport,
  type RecordingControlTracking,
} from '@/lib/telemetry/recordingControlTracking';
import {
  beginNativeRecordingFinalizeAttempt,
  cancelNativeRecordingFinalizeAttempt,
  completeNativeRecordingFinalizeAttempt,
  type NativeFinalizeResult,
} from '@/lib/telemetry/recordingFinalizeTracking';
import { pTimeout } from '@/lib/utils/pTimeout';
import { sleep } from '@/lib/utils/sleep';
import { USE_CHUNK_RECORDING } from '@/models/scribeRecordModel';
import { UploadStatus } from '@/models/tauriRecordModel';
import { store, type Dispatch, type RootState } from '@/store';
import { recorderFmc } from '@/tauri/controller/recording';
import type { StopReason } from '@/tauri/controller/self-healing-stop';
import { detachOnlineChunkUploadListeners } from '@/tauri/online-chunk-upload/listeners';
import {
  onlineChunkUploadBridge,
  type StopOnlineChunkUploadResult,
} from '@/tauri/online-chunk-upload/session';
import {
  classifyMicLabelForTelemetry,
  isCaptureNeverEnergetic,
  resolveInUseMicLabel,
} from '@/tauri/silentCapture';
import { isTauri } from '@/tauri/utils';
import { isOnPrem } from '@/utils/buildProfile';
import chunkAudioRecorder from '@/utils/chunkAudioRecorder';
import { logger } from '@/utils/logger/logger';
import {
  offlineHandoverForStop,
  settleOfflineHandover,
} from '@/utils/offline-recording/offlineHandoverStop';
import { isTranscribeLikeMode } from '@/utils/transcribeMode';
import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import {
  fenceMidRecordingRestarts,
  fenceRecordingStarts,
  forgetMidRecordingRestarts,
  holdRecordingStartsForStop,
  inFlightRecordingStartKinds,
  isMidRecordingRestartRecording,
  type RecordingStartFence,
  type RecordingStartOutcome,
  type StandDownStopFacts,
} from '@heidi/capture';
import { toast } from '@heidi/design-system/toast';
import { bridgeErrorKind, recordingLifecycle } from '@heidi/plugin-core';
import { sanitizeError } from '@heidi/runtime/logger';
import { GenerationMethod, TemplateCategory } from '@heidi/schema/ml-scribe';
import {
  BackupUploadingStatus,
  GET_SESSION_BY_ID,
  StreamingStatus,
  type QuerySessionResponse,
} from '@heidi/session/contracts';
import {
  beginArtifactGeneration,
  completeArtifactGeneration,
  rememberClientRustStop,
  resetArtifactGeneration,
  stopSessionGeneration,
  useQuerySessionById,
} from '@heidi/session/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useAtom, useSetAtom } from 'jotai';
import { get } from 'lodash-es';
import { isMobile, isTablet } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import {
  invalidateLiveTranscript,
  setIsPollingChunks,
  setSessionChunkUploadProgress,
} from '../query/useQueryLiveTranscriptBySessionId';
import { useSessionWindows } from '../query/useQuerySessionWindows';
import { useQueryTemplateById } from '../query/useQueryTemplateById';
import { GET_TRANSCRIPT_BY_SESSION_ID } from '../query/useQueryTranscriptBySessionId';
import { STOP_RECORDING_MUTATION_KEY } from './recordingMutationKeys';
import {
  beginRustChunkUploadPauseFinalization,
  clearRustChunkUploadPauseFinalization,
  getRustChunkUploadPauseFinalizationOutcome,
  resolveChunkFailureThreshold,
  shouldDetachBackupFromStop,
} from './rustChunkUploadPauseFinalization';
import {
  outcomeMayGenerateNote,
  peekTerminalOutcome,
  terminalOutcomeAsStopResult,
  type NativeTerminalOutcome,
} from './terminalOutcomeRelay';
import useMutateCreateDocumentFromNote from './useMutateCreateDocumentFromNote';
import { isPTimeoutRejection } from './useMutateStartTauriRecording.constants';
import { useMutateUpdateSessionSocketError } from './useMutateUpdateSessionSocketError';
import {
  TRUNCATED_OUTCOME_LOG,
  useMutateUploadBackupRecording,
} from './useMutateUploadBackupRecording';
import { useMutateGetAndUploadChunkRecording } from './useMutateUploadChunkRecording';
import { usePersistAuthoritativeDuration } from './usePersistAuthoritativeDuration';

/** Attempts for the blocking Rust stop IPC before falling back to a direct recorder stop. */
const STOP_SESSION_MAX_ATTEMPTS = 3;
/** Backoff between attempts grows linearly: 2s, then 4s. */
const STOP_SESSION_RETRY_BASE_DELAY_MS = 2000;
/** Covers recorder stop, final chunk upload, backup extract/upload, and `:finish`. */
const STOP_SESSION_FIRST_ATTEMPT_TIMEOUT_MS = 900000;
/** Retries resolve fast stop/start race failures; a wedged retry gains nothing from a long cap. */
const STOP_SESSION_RETRY_TIMEOUT_MS = 15000;
/** Best-effort cross-engine sweep on the legacy path; a hang must not stall the stop. */
const LEGACY_SWEEP_TIMEOUT_MS = 10000;
/** Headroom past the native start budget for the upload session to come up. */
const RECONCILE_RESTART_SETTLE_MARGIN_MS = 15000;
/** The epoch read is a state read; a slow answer must not stall the stop. */
const RECONCILE_SNAPSHOT_TIMEOUT_MS = 5000;
export const RECORDER_SAFETY_STOP_DELAY_MS = 5000;

export type StopRecordingInput = {
  isPaused?: boolean;
  /** Native capture already stopped; stop any late restart, then run the finalization tail. */
  reconcileAlreadyStopped?: boolean;
  /** The WAV is quarantined and must not be read, finalized, or salvaged. */
  wavWriterFailed?: boolean;
  /** Native capture stopped early while the UI showed recording. Blocks note generation. */
  captureTruncated?: boolean;
  /** No source carried audio. Blocks note generation and opens the mic reselection prompt. */
  silentCapture?: { micLabel?: string };
  /** The pause or stop attempt the press opened. Without one, the stop closes no attempt. */
  tracking?: RecordingControlTracking;
};

/** Reports a stop or pause that failed at the audio layer, then returns the error to throw. */
function failDesktopStopAttempt(
  tracking: RecordingControlTracking | undefined,
  message: string
): DesktopCaptureError {
  const error = new DesktopCaptureError(
    DesktopCaptureFailure.RECORDER_STOP_FAILED,
    message
  );
  // Close at the audio failure, before the mutation's generic error handling.
  finishRecordingControlAttempt(tracking, { outcome: 'failed', error });
  return error;
}

/**
 * What a Rust stop tells the transcript claim. No result, or one replayed from a stop with
 * another reason, vouches for nothing. Rust decides `pendingAsync` only after `:finish`
 * succeeded (`terminal.rs` `select_backup_mode`): a detached backup, or no backup with every
 * chunk uploaded. Either way the transcript is server-side.
 */
function rustStopClaimSignal(
  result: StopOnlineChunkUploadResult | undefined,
  sentReason: StopReason
) {
  const ownOutcome = result?.reason === undefined || result.reason === sentReason;
  return {
    failedChunks: result?.failedChunks ?? 0,
    backupUploaded: ownOutcome && result?.backupUploaded === true,
    backupPendingAsync: ownOutcome && result?.backup === 'pendingAsync',
  };
}

const STOP_FAILED_ALERT =
  'Recording could not be stopped and the microphone may still be capturing. ' +
  'Please press End again to retry. If the problem persists, close the Heidi app.';

/** Whether the native recorder is capturing. An unreadable state counts as live. */
async function isRecorderStillRecording(): Promise<boolean> {
  try {
    const recordingState = await pTimeout(recorderFmc.get_recording_state(), 5000);
    return recordingState === 'Recording';
  } catch {
    return true;
  }
}

/**
 * Only a final stop sends the settings, so only a final stop can be handed
 * over; the pause guard keeps a recovered outcome honest too.
 */
function handedOverLocalSessionIdOf(
  facts: StopOnlineChunkUploadResult | undefined,
  isPaused: boolean
): string | undefined {
  return isPaused ? undefined : facts?.offlineHandoverLocalSessionId;
}

/** What the finalize reads off a stop answer, or off nothing. */
function nativeFinalizeFacts(
  facts: StopOnlineChunkUploadResult | undefined,
  isPaused: boolean
): NativeFinalizeResult {
  return {
    stopFailed: facts === undefined,
    failedChunks: facts?.failedChunks,
    finishSucceeded: facts?.finishSucceeded,
    backupUploaded: facts?.backupUploaded,
    offlineHandover: handedOverLocalSessionIdOf(facts, isPaused) !== undefined,
  };
}

/**
 * Asks native what happened after `stopSession()` attempts ran out.
 * `undefined` means native never reported, so the FE may infer from its own clock.
 */
// The typed outcome carries no `sessionId`, so the epoch comes from the snapshot.
async function recoverTypedOutcome() {
  try {
    const snapshot = await pTimeout(recorderFmc.getRecordingStateSnapshot(), 5000);
    if (!snapshot?.recordingId) return undefined;
    return peekTerminalOutcome(snapshot.recordingId, snapshot.generation);
  } catch (error) {
    logger.warn(
      '[StopRecording] could not resolve the epoch to recover a typed outcome',
      error
    );
    return undefined;
  }
}

/** Bounded, PHI-free fields for a bridge rejection; the message is never read. */
function reconcileErrorFields(error: unknown) {
  return {
    errorKind: bridgeErrorKind(error),
    timedOut: isPTimeoutRejection(error),
    ...sanitizeError(error),
  };
}

/** The reason a fenced start's own stand-down stops with; only a pause stays a pause. */
const toFenceReason = (reason: StopReason): RecordingStartFence['reason'] =>
  reason === 'pause' ? 'pause' : 'userStop';

/** Each restart's outcome, or undefined for one still starting when the budget ran out. */
async function waitForRestarts(
  pending: Promise<RecordingStartOutcome>[],
  budgetMs: number
): Promise<(RecordingStartOutcome | undefined)[]> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const expired = new Promise<undefined>((resolve) => {
    timer = setTimeout(() => resolve(undefined), budgetMs);
  });
  try {
    return await Promise.all(pending.map((settled) => Promise.race([settled, expired])));
  } finally {
    clearTimeout(timer);
  }
}

/**
 * The live native epoch when a restart of the session published it; any other epoch
 * is not the reconcile's to stop. Ownership is read fresh, so a user start disarms it.
 */
async function readOwnedEpoch(sessionId: string) {
  try {
    const epoch = await pTimeout(
      recorderFmc.getRecordingStateSnapshot(),
      RECONCILE_SNAPSHOT_TIMEOUT_MS
    );
    return epoch.state === 'active' &&
      isMidRecordingRestartRecording(sessionId, epoch.recordingId)
      ? epoch
      : undefined;
  } catch (error) {
    // Ownership cannot be proven, so nothing is stopped; the watchdog backs this up.
    logger.error('[StopRecording] Reconcile could not read the native epoch', {
      sessionId,
      ...reconcileErrorFields(error),
    });
    return undefined;
  }
}

/** Last resort when the late-restart stop failed. Ownership is re-read before each stop. */
async function killOwnedCapture(
  selfHealingEnabled: boolean,
  sessionId: string
): Promise<void> {
  let epoch = await readOwnedEpoch(sessionId);
  if (!epoch) return;
  if (selfHealingEnabled) {
    try {
      // Owner-scoped: Rust rejects it as stale once a newer epoch holds the recorder.
      const outcome = await recorderFmc.stopAndFinalize({
        recordingId: epoch.recordingId,
        generation: epoch.generation,
        reason: 'feFallback',
      });
      if (outcome?.recorderStopped === true) {
        logger.warn(
          '[StopRecording] Reconcile stopped a late restart through its epoch',
          {
            sessionId,
          }
        );
        return;
      }
    } catch (error) {
      logger.warn('[StopRecording] Reconcile epoch stop failed', {
        sessionId,
        ...reconcileErrorFields(error),
      });
    }
    epoch = await readOwnedEpoch(sessionId);
    if (!epoch) return;
  }
  if (!(await isRecorderStillRecording())) return;
  try {
    await pTimeout(recorderFmc.stop(), 60000);
    logger.warn('[StopRecording] Reconcile stopped live capture directly', { sessionId });
  } catch (error) {
    logger.error('[StopRecording] Reconcile could not stop live capture', {
      sessionId,
      ...reconcileErrorFields(error),
    });
  }
}

/** What the reconcile's late-restart stop leaves for the backup decision. */
type LateRestartStop = {
  /** Backup facts of whichever stop ran, when one answered. */
  facts: StandDownStopFacts | undefined;
  /** A native stop is still running and owns the WAVs, so the FE must not re-extract them. */
  nativeStillStopping: boolean;
};

/**
 * Reconcile runs when the FE believes capture already stopped, but a slow device-switch
 * restart can still bring it back with no owner. Fence this session's restarts so a late
 * one stops itself, wait for them, then stop only a recording a restart published.
 */
async function stopLateRestartForReconcile(
  sessionId: string,
  opts: { reason: StopReason; quarantineWav: boolean; selfHealingEnabled: boolean }
): Promise<LateRestartStop> {
  const pending = fenceMidRecordingRestarts(sessionId, {
    quarantineWav: opts.quarantineWav,
    reason: toFenceReason(opts.reason),
  });
  const waitBudgetMs =
    getDesktopRecorderStartTimeoutMs() + RECONCILE_RESTART_SETTLE_MARGIN_MS;
  const outcomes = await waitForRestarts(pending, waitBudgetMs);
  // A restart still starting will stop itself, and its native stop then owns the WAVs.
  const restartStillStarting = outcomes.includes(undefined);
  if (restartStillStarting) {
    logger.warn('[StopRecording] Reconcile wait expired with a restart still starting', {
      sessionId,
      waitBudgetMs,
    });
  }
  const standDowns = outcomes.filter(
    (outcome): outcome is Extract<RecordingStartOutcome, { kind: 'stoodDown' }> =>
      outcome?.kind === 'stoodDown'
  );
  const nativeStillStopping =
    restartStillStarting || standDowns.some((standDown) => standDown.stillStopping);
  const standDownFacts = standDowns.find((standDown) => standDown.stop)?.stop;
  const restartPublished = isMidRecordingRestartRecording(sessionId);
  const epoch = restartPublished ? await readOwnedEpoch(sessionId) : undefined;
  if (!epoch) {
    logger.info('[StopRecording] Reconcile found no live restart to stop', {
      sessionId,
      restartPublished,
      restartStoodDown: standDowns.length > 0,
      nativeStillStopping,
    });
    forgetMidRecordingRestarts(sessionId);
    return { facts: standDownFacts, nativeStillStopping };
  }
  try {
    // The same budget as an ordinary stop: the backup upload inside it can take minutes.
    const result = await pTimeout(
      onlineChunkUploadBridge.stopSession({
        reason: opts.reason,
        quarantineWav: opts.quarantineWav,
      }),
      STOP_SESSION_FIRST_ATTEMPT_TIMEOUT_MS
    );
    logger.warn('[StopRecording] Reconcile stopped a restart that finished late', {
      sessionId,
      failedChunks: result.failedChunks,
      finishSucceeded: result.finishSucceeded,
      backupUploaded: result.backupUploaded,
      backupPendingAsync: result.backupPendingAsync,
    });
    return { facts: result, nativeStillStopping };
  } catch (error) {
    const timedOut = isPTimeoutRejection(error);
    logger.error('[StopRecording] Reconcile stop of a late restart failed', {
      sessionId,
      ...reconcileErrorFields(error),
    });
    if (timedOut) {
      // The native stop keeps running; use its outcome if it has stored one.
      const outcome = peekTerminalOutcome(epoch.recordingId, epoch.generation);
      return outcome
        ? { facts: terminalOutcomeAsStopResult(outcome), nativeStillStopping }
        : { facts: standDownFacts, nativeStillStopping: true };
    }
    await killOwnedCapture(opts.selfHealingEnabled, sessionId);
    return { facts: standDownFacts, nativeStillStopping };
  } finally {
    forgetMidRecordingRestarts(sessionId);
  }
}

/**
 * Stop fallback for a wedged `stopSession` IPC: try `stopAndFinalize` first, then
 * fall back to a direct `recorderFmc.stop()` unless it reports `recorderStopped`.
 */
async function stopRecorderWithUnifiedFallback(
  selfHealingEnabled: boolean,
  sessionId: string
): Promise<void> {
  if (selfHealingEnabled) {
    try {
      const snapshot = await recorderFmc.getRecordingStateSnapshot();
      const outcome = await recorderFmc.stopAndFinalize({
        recordingId: snapshot.recordingId,
        generation: snapshot.generation,
        reason: 'feFallback',
      });
      if (outcome?.recorderStopped === true) {
        logger.info(
          '[StopRecording] unified terminal command (feFallback) confirmed the recorder stopped',
          { sessionId }
        );
        return;
      }
    } catch (unifiedStopError) {
      logger.warn(
        '[StopRecording] unified terminal command (feFallback) failed, falling back to direct recorder stop:',
        unifiedStopError
      );
    }
  }
  await pTimeout(recorderFmc.stop(), 60000);
}

/** Safety net from `onSettled`: force-stops a recorder still live. Skipped while the timer runs. */
export async function runRecorderSafetyStop(sessionId: string): Promise<void> {
  try {
    if (store.getState().timerModel.isRunning) {
      return;
    }
    const recordingState = await pTimeout(recorderFmc.get_recording_state(), 5000);
    if (recordingState !== 'Recording') {
      return;
    }
    // Re-check after the await: a recording started during the probe owns the
    // recorder. The Rust stop tombstone covers the remaining tiny window.
    if (store.getState().timerModel.isRunning) {
      return;
    }
    logger.warn(
      '[StopRecording] Safety stop: recorder still live after stop settled, forcing stop',
      { sessionId }
    );
    try {
      await recorderFmc.stop_without_backup_file();
    } catch (stopError) {
      // A live recorder the last resort cannot stop must surface to the user.
      logger.error('[StopRecording] Safety stop failed on a live recorder:', stopError);
      alert(STOP_FAILED_ALERT);
    }
  } catch (error) {
    logger.error('[StopRecording] Safety stop probe failed:', error);
  }
}

/**
 * Mutation that stops a recording session on Tauri, web and mobile.
 *
 * @beta
 */
export function useMutateStopRecording({ sessionId }: { sessionId: string }) {
  const queryClient = useQueryClient();
  const dispatch = useDispatch<Dispatch>();
  const setUploadRequiredModal = useSetAtom(requireBackupUploadModalAtom);
  const setBackupUploadPrompt = useSetAtom(backupUploadPromptAtom);
  const setNativeBackupUploadGate = useSetAtom(nativeBackupUploadGateAtom);
  const setPreStartSystemMutePopoverOpen = useSetAtom(preStartSystemMutePopoverOpenAtom);
  const setPreStartSystemMuteUnhealthy = useSetAtom(preStartSystemMuteUnhealthyAtom);
  const [activeRecordingTransport, setActiveRecordingTransport] = useAtom(
    activeRecordingTransportAtom
  );
  const isTabSharingSession = useCurrentSessionIsTabSharing();
  const offlineRecordingEnabled = useIsOfflineRecordingEnabled();

  const { mutate: uploadBackupRecording } = useMutateUploadBackupRecording();
  const persistAuthoritativeDuration = usePersistAuthoritativeDuration({ sessionId });
  const { mutate: markSessionHadWebsocketError } = useMutateUpdateSessionSocketError();
  const { mutate: createEmptyDocumentFromNote } = useMutateCreateDocumentFromNote({
    sessionId,
  });
  const { mutateAsync: getChunkRecordingAndUpload } =
    useMutateGetAndUploadChunkRecording();

  const { data: session } = useQuerySessionById({
    sessionId,
    select: (data) => {
      if (!data) return undefined;
      return {
        transcribe_mode: data.transcribe_mode,
        consult_note: data.consult_note,
        session_id: data.session_id,
        isSocketError: data.isSocketError,
        had_ws_recording_failure: data.had_ws_recording_failure,
        recording_status: data.recording_status,
      };
    },
  });

  // get the default template for the session if it exists
  const { data: template } = useQueryTemplateById(
    session?.consult_note?.template_id,
    session?.consult_note?.generation_method
  );
  const { chunks } = useSelector((rootState: RootState) => rootState.tauriRecordModel);
  // const [finalizing, finalizing] = useAtom(isPollingChunksAtom);

  const { mergeSessionWindows } = useSessionWindows({
    sessionId,
  });

  /** Marks the backup upload failed so the modal shows recovery actions, not a stuck bar. */
  const settleTerminalBackupRecovery = () => {
    // Fence any generation racing the terminal event so it cannot restore stale Processing.
    stopSessionGeneration(sessionId, 'recovery');
    queryClient.setQueryData<QuerySessionResponse>(
      [GET_SESSION_BY_ID, { sessionId }],
      (oldData) =>
        oldData
          ? {
              ...oldData,
              backupUploadingStatus: BackupUploadingStatus.FAILED,
              streamingStatus: StreamingStatus.NOT_STARTED,
              chunkUploadProgress: undefined,
              abortController: new AbortController(),
            }
          : undefined
    );
  };

  const settleWavWriterFailure = () => {
    settleTerminalBackupRecovery();
  };

  const openCaptureTruncatedRecovery = () => {
    settleTerminalBackupRecovery();
    setBackupUploadPrompt({ kind: 'captureTruncated', sessionId });
  };

  const openSilentCaptureRecovery = (micLabel: string | undefined) => {
    settleTerminalBackupRecovery();
    setBackupUploadPrompt({ kind: 'silentCapture', sessionId, micLabel });
  };

  /**
   * Did this recording end with no usable audio? Explicit from the hard stop, or
   * derived from this session's never-energetic latches. Never on a pause.
   */
  const resolveSilentCapture = (
    input: StopRecordingInput | undefined,
    entryState: RootState
  ): { micLabel: string | undefined } | undefined => {
    if (input?.silentCapture) {
      return { micLabel: input.silentCapture.micLabel };
    }
    if (input?.isPaused || !isDesktopSilentMicHardStopEnabled()) {
      return undefined;
    }
    const owner = entryState.tauriRecordModel.chunks.session_id;
    if (owner !== sessionId) {
      return undefined;
    }
    if (!isCaptureNeverEnergetic(entryState.tauriRecordModel.captureNeverEnergetic)) {
      return undefined;
    }
    return {
      micLabel: resolveInUseMicLabel({
        selected: entryState.scribeRecordModel.tauriInputDeviceSelected,
        devices: entryState.scribeRecordModel.tauriInputDevices,
        lockedInputDeviceName:
          entryState.tauriRecordModel.lockedStartDevices?.inputDeviceName,
      }),
    };
  };

  // Truncated capture: try to upload the captured audio. Skipped when Rust
  // already owns the backup (uploaded, detached or retained) or moved the
  // source WAVs, as the re-upload branch below does.
  const uploadTruncatedBackup = async (skipUpload: boolean, isPaused?: boolean) => {
    if (skipUpload) return;
    // Nothing was uploaded: flag an open prompt so its Close hands over, or raise
    // the recovery prompt if the user already closed it.
    const markUploadFailed = () =>
      setBackupUploadPrompt((current) =>
        isCaptureTruncatedPromptFor(current, sessionId)
          ? { ...current, uploadFailed: true }
          : (current ?? { kind: 'frontendUpload', sessionId, partial: true })
      );
    try {
      const res = await dispatch.tauriRecordModel.get_backup_file();
      if (res?.blob && res.path && res.blob.size > 0) {
        uploadBackupRecording({
          sessionId,
          rawAudio: res.blob,
          audioId: getUniqueId(),
          mimeType: 'audio/mp3',
          inBackground: true,
          generateNoteOnSuccess: false,
          sessionRecordingEnded: !isPaused,
          promptOnFailure: true,
          partialAudio: true,
          path: res.path,
        });
        logger.info(TRUNCATED_OUTCOME_LOG, { outcome: 'uploadQueued' });
      } else {
        logger.warn('[StopRecording] capture-truncated backup file missing or empty', {
          sessionId,
        });
        logger.info(TRUNCATED_OUTCOME_LOG, { outcome: 'fileMissing' });
        markUploadFailed();
      }
    } catch (error) {
      logger.error('[StopRecording] capture-truncated backup file read failed', {
        sessionId,
        errorKind: bridgeErrorKind(error),
        error: sanitizeError(error),
      });
      logger.info(TRUNCATED_OUTCOME_LOG, { outcome: 'fileReadFailed' });
      markUploadFailed();
    }
  };

  /** Releases the optimistic `UPLOADING` when no note generation or backup upload owns it. */
  const releaseUploadingWhenUnowned = (input?: StopRecordingInput) => {
    // `onMutate` skips a pause, and the value compare keeps a status a later owner advanced.
    // The compare is safe only because each call site sits on a branch with no other
    // `UPLOADING` owner. A branch that can re-enter `UPLOADING` would need an epoch instead.
    if (input?.isPaused) return;
    completeArtifactGeneration({ kind: 'note', sessionId });
    queryClient.setQueryData<QuerySessionResponse>(
      [GET_SESSION_BY_ID, { sessionId }],
      (oldData) =>
        oldData?.streamingStatus === StreamingStatus.UPLOADING
          ? { ...oldData, streamingStatus: StreamingStatus.NOT_STARTED }
          : oldData
    );
  };

  // recording_stop ends when local capture is confirmed stopped. Later upload and
  // note generation health stays in DESKTOP_RECORDING_STOP_OUTCOME. The Rust
  // stopSession path closes after its flush (one IPC).
  const finishStopAtCaptureStop = ({
    tracking,
    timerSeconds,
    rustDurationMs,
    detachedBackupMux,
  }: {
    tracking: RecordingControlTracking | undefined;
    timerSeconds: number;
    rustDurationMs?: number;
    detachedBackupMux: boolean;
  }) => {
    const cachedDuration = queryClient.getQueryData<QuerySessionResponse>([
      GET_SESSION_BY_ID,
      { sessionId },
    ])?.duration;
    const recordingDurationS = Math.round(
      Math.max(
        Number(timerSeconds) || 0,
        typeof cachedDuration === 'number' ? cachedDuration : 0,
        (rustDurationMs ?? 0) / 1000
      )
    );
    finishRecordingControlAttempt(tracking, {
      metadata: {
        ...(recordingDurationS > 0 ? { recordingDurationS } : {}),
        // Unconditional: stop latency is split by this rollout cohort.
        detachedBackupMux,
      },
    });
  };

  // Pause ends at the same boundary. An unhealthy stop is a failed pause.
  const finishPauseAtCaptureStop = (
    tracking: RecordingControlTracking | undefined,
    captureStopUnhealthy: boolean
  ) => {
    if (captureStopUnhealthy) {
      finishRecordingControlAttempt(tracking, {
        outcome: 'failed',
        error: new DesktopCaptureError(
          DesktopCaptureFailure.RECORDER_STOP_FAILED,
          'Desktop recording stop was unhealthy'
        ),
      });
    } else {
      finishRecordingControlAttempt(tracking);
    }
  };

  return useMutation({
    mutationKey: [STOP_RECORDING_MUTATION_KEY],
    networkMode: 'offlineFirst',
    mutationFn: async (
      input?: StopRecordingInput
    ): Promise<{
      stopUnhealthy: boolean;
      detachedBackupMux: boolean;
    }> => {
      setRecordingControlTransport(input?.tracking, localCaptureTransport());
      // Lets onSuccess release the note slot: an unhealthy stop makes no note.
      let stopUnhealthy = false;
      /** The recorder stop itself failed (not just chunk-upload or backup health). */
      let captureStopUnhealthy = false;
      // Rollout cohort of the stop path: the value actually passed to `stopSession`,
      // not the kill switch. Stays `false` on the reconcile, legacy and web paths.
      let detachedBackupMux = false;
      // Sample the truncation inputs before the first `await`, from one store snapshot,
      // since another session's start overwrites these shared slots.
      const entryState = store.getState();
      const seconds = entryState.timerModel.seconds;
      const lockedChunkInterval = entryState.tauriRecordModel.lockedChunkInterval;
      // Same entry snapshot: the latches are wiped after the native stop.
      const silentCapture = resolveSilentCapture(input, entryState);
      if (silentCapture) {
        // The mic label can contain a person's name, so it is classified, never sent.
        logger.warn('[StopRecording] Recording ended with no usable audio', {
          sessionId,
          ...classifyMicLabelForTelemetry({
            micLabel: silentCapture.micLabel,
            lockedInputDeviceName:
              entryState.tauriRecordModel.lockedStartDevices?.inputDeviceName,
          }),
          explicit: Boolean(input?.silentCapture),
          wallClockSecs: seconds,
        });
      }
      // V2 flag locked at start, read only if it belongs to this session (else v1).
      // The owner is `chunks.session_id` because it is set together with the V2 flag.
      const transcriptionServiceV2Owner = entryState.tauriRecordModel.chunks?.session_id;
      const usesTranscriptionServiceV2 =
        transcriptionServiceV2Owner === sessionId &&
        Boolean(entryState.tauriRecordModel.useTranscriptionServiceV2);
      const shouldUseDocumentFlow =
        isTranscribeLikeMode(session?.transcribe_mode) &&
        !input?.isPaused &&
        ((!isGenerateConsultNoteFromDocumentTemplateEnabled() &&
          template?.template_category === TemplateCategory.DOCUMENT_TEMPLATE) ||
          (IS_RELEASE_FORM_FILLING_FLOW_ALIGNMENT_ENABLED() &&
            template?.template_category === TemplateCategory.FORM_TEMPLATE));

      const usingRustUpload =
        isTauri() && Boolean(store.getState().tauriRecordModel.useRustChunkUpload);
      // Read once, before a fenced start can stand down, so its session stop and
      // this stop send Rust the same handover.
      const offlineHandover = offlineHandoverForStop({
        sessionId,
        isPaused: Boolean(input?.isPaused),
        offlineRecordingEnabled,
        usesDocumentFlow: shouldUseDocumentFlow,
        hasOwnRecoveryPrompt:
          Boolean(silentCapture) ||
          Boolean(input?.wavWriterFailed) ||
          input?.captureTruncated === true,
        queryClient,
      });
      // Read once per stop (imperative, so it works inside this async mutationFn).
      // Default OFF leaves every branch below unchanged.
      const selfHealingEnabled = isTauri() && isSelfHealingStopEnabled();
      const failedChunkThreshold = selfHealingEnabled ? getFailedChunkThreshold() : 0;
      const pauseFinalization =
        input?.isPaused && usingRustUpload
          ? beginRustChunkUploadPauseFinalization(sessionId)
          : undefined;
      if (!input?.isPaused && usingRustUpload) {
        clearRustChunkUploadPauseFinalization(sessionId);
      }

      // Set once this stop fenced a start: a later start of the session waits until
      // this stop's own session stop is done, so a retry cannot stop that start.
      let releaseFencedStarts: (() => void) | undefined;
      // The pause hand-off must leave `openResolves` on every exit path, throws
      // included. The sweep stands aside while a hand-off is open, so a stranded
      // entry would turn it off for that session. `resolve` is idempotent.
      try {
        setIsPollingChunks(sessionId);
        if (isTauri()) {
          logger.info('[StopRecording] User initiated stop', {
            sessionId,
            reconcileAlreadyStopped: Boolean(input?.reconcileAlreadyStopped),
          });

          if (!input?.reconcileAlreadyStopped) {
            // Before the first await: fence this session's in-flight starts, so a late one
            // stands down instead of capturing with no owner. Not awaited here.
            // The kill switch restores the earlier stop, which fenced no start.
            const fenceKilled = isUserStartFenceKilled();
            const startKinds = fenceKilled ? [] : inFlightRecordingStartKinds(sessionId);
            const fence: RecordingStartFence = {
              quarantineWav: Boolean(input?.wavWriterFailed),
              reason: toFenceReason(input?.isPaused ? 'pause' : 'userStop'),
              // Read only when a start can stand down and send them.
              stopSettings:
                startKinds.length > 0
                  ? {
                      detachedBackupMux: shouldDetachBackupFromStop({
                        detachedBackupMuxEnabled: isDetachedBackupMuxEnabled(),
                        usesTranscriptionServiceV2,
                        isPaused: Boolean(input?.isPaused),
                      }),
                      offlineHandover,
                    }
                  : undefined,
            };
            const fencedStarts = fenceKilled
              ? []
              : fenceRecordingStarts(sessionId, fence);
            if (fencedStarts.length > 0) {
              releaseFencedStarts = holdRecordingStartsForStop(sessionId);
              logger.warn('[StopRecording] Fenced a start still in flight', {
                sessionId,
                fencedStarts: fencedStarts.length,
                startKinds,
                fenceReason: fence.reason,
              });
            }
            let recordingState: string | null = null;
            try {
              recordingState = await pTimeout(recorderFmc.get_recording_state(), 5000);
              logger.info('[StopRecording] Pre-check state', { recordingState });
            } catch (preCheckError) {
              logger.error(
                '[StopRecording] Pre-check get_recording_state failed or timed out, proceeding with stop',
                { error: sanitizeError(preCheckError) }
              );
            }

            if (recordingState !== null && recordingState !== 'Recording') {
              // Native stops are idempotent; stop both engines regardless of
              // the reported state.
              logger.warn(
                '[StopRecording] Recording not in expected state, stopping anyway',
                {
                  recordingState,
                  sessionId,
                }
              );
              trackTauriStopRecordingPreventedAsRecordingStillInitializing();
            }
          }

          sessionStopRecording({
            sessionId: sessionId,
            duration: seconds,
            isTabSharing: isTabSharingSession,
          });
          // Locked at session start. When Rust owns chunk upload, stop is one blocking
          // IPC (stop, final chunk + backup upload, `:finish`); otherwise the legacy FE
          // path stops the recorder and flushes.
          if (usingRustUpload) {
            dispatch.timerModel.onPause();
            // Hidden as `rust_stop_in_progress` until this stop records its result.
            rememberClientRustStop(sessionId, 'in_progress');
            // Opened before the stop IPC and any `await`, so every recovery_update settle
            // is seen against an open watch. The later raise is a compare-and-set, so
            // settle-then-raise and raise-then-settle end in the same state.
            setNativeBackupUploadGate((gate) =>
              openNativeBackupUploadWatch(gate, sessionId)
            );
            if (isOnPrem) {
              // Keep live transcript on in_progress until Rust stop/finish completes.
              setSessionChunkUploadProgress(sessionId, 0);
            }
            let stopResult: StopOnlineChunkUploadResult | undefined;
            // A reconcile's late-restart stop. Only its backup facts are read: they skip a
            // re-upload of audio Rust already sent and let that audio generate the note.
            let lateRestartStop: StandDownStopFacts | undefined;
            // A native stop the reconcile could not wait out still owns the WAVs.
            let nativeStopStillRunning = false;
            let stopThrew = false;
            let lastStopError: unknown;
            // Hoisted out of the retry loop: the branch that reads the result
            // needs the same settings the stop sent.
            const sentReason: StopReason = input?.isPaused ? 'pause' : 'userStop';
            // The finalize attempt spans the stop IPC, which drains, backs up and
            // calls `:finish`. A reconcile's late-restart stop is best effort and only
            // its backup facts are read, so it opens none.
            const finalizeAttempt = input?.reconcileAlreadyStopped
              ? undefined
              : beginNativeRecordingFinalizeAttempt(input?.isPaused ? 'pause' : 'stop');
            if (input?.reconcileAlreadyStopped) {
              // A late start can revive capture, so the stop must still reach Rust.
              // `stopResult` stays undefined, so the chunk health gate never passes;
              // only a backup Rust confirmed server-side lets the note generate.
              logger.warn(
                '[StopRecording] Reconciling an already-stopped recorder; stopping any late restart',
                { sessionId }
              );
              const lateRestart = await stopLateRestartForReconcile(sessionId, {
                reason: sentReason,
                quarantineWav: Boolean(input?.wavWriterFailed),
                selfHealingEnabled,
              });
              lateRestartStop = lateRestart.facts;
              nativeStopStillRunning = lateRestart.nativeStillStopping;
              // A late start can re-arm the timer after the pause above.
              dispatch.timerModel.onPause();
            } else {
              // A stop racing a slow start commit rejects before the session is
              // Active; a retry seconds later succeeds. Retries log at warn;
              // exactly one error fires on final exhaustion.
              for (let attempt = 1; attempt <= STOP_SESSION_MAX_ATTEMPTS; attempt++) {
                try {
                  // Resolved per attempt so a flag flip reaches the next retry.
                  detachedBackupMux = shouldDetachBackupFromStop({
                    detachedBackupMuxEnabled: isDetachedBackupMuxEnabled(),
                    usesTranscriptionServiceV2,
                    isPaused: Boolean(input?.isPaused),
                  });
                  stopResult = await pTimeout(
                    // A pause is a segment stop: it finalizes and retains but never
                    // generates a note. The reason lands in the terminal outcome.
                    onlineChunkUploadBridge.stopSession({
                      reason: sentReason,
                      // A V2 final stop detaches the backup; a pause does not. v1 follows
                      // the kill switch, read per stop. Rust decides what actually happens.
                      detachedBackupMux,
                      offlineHandover,
                    }),
                    attempt === 1
                      ? STOP_SESSION_FIRST_ATTEMPT_TIMEOUT_MS
                      : STOP_SESSION_RETRY_TIMEOUT_MS
                  );
                  stopThrew = false;
                  logger.info('[StopRecording] Rust online chunk upload stopped', {
                    sessionId,
                    attempt,
                    failedChunks: stopResult?.failedChunks,
                    backupUploaded: stopResult?.backupUploaded,
                    // Tells a granted detach apart from a failed inline backup (both
                    // report `backupUploaded: false`). A boolean, so no PHI.
                    backupPendingAsync: stopResult?.backupPendingAsync,
                    backupDeferred: stopResult?.backupDeferred,
                    finishSucceeded: stopResult?.finishSucceeded,
                    offlineHandedOver: Boolean(stopResult?.offlineHandoverLocalSessionId),
                  });
                  break;
                } catch (stopError) {
                  stopThrew = true;
                  lastStopError = stopError;
                  logger
                    .child({ attempt, maxAttempts: STOP_SESSION_MAX_ATTEMPTS })
                    .warn(
                      '[StopRecording] online chunk upload stopSession failed or timed out',
                      { error: sanitizeError(stopError) }
                    );
                  if (attempt < STOP_SESSION_MAX_ATTEMPTS) {
                    await sleep(STOP_SESSION_RETRY_BASE_DELAY_MS * attempt);
                  }
                }
              }
              if (stopThrew) {
                logger
                  .child({ sessionId, attempts: STOP_SESSION_MAX_ATTEMPTS })
                  .error(
                    '[StopRecording] online chunk upload stopSession exhausted retries',
                    { error: sanitizeError(lastStopError) }
                  );
                // Kill capture directly; the mic must not outlive the stop even when
                // the upload session is wedged (unified terminal command first).
                try {
                  await stopRecorderWithUnifiedFallback(selfHealingEnabled, sessionId);
                  logger.warn(
                    '[StopRecording] stopSession exhausted retries; stop fallback succeeded',
                    { sessionId }
                  );
                } catch (recorderStopError) {
                  logger.warn(
                    '[StopRecording] direct recorder stop also failed, capture may still be live',
                    { error: sanitizeError(recorderStopError) }
                  );
                  if (await isRecorderStillRecording()) {
                    // Capture death is unconfirmed: restore the recording UI
                    // (the timer drives the button) and fail loudly.
                    logger
                      .child({
                        sessionId,
                        path: 'rust',
                        attempts: STOP_SESSION_MAX_ATTEMPTS,
                      })
                      .error('[StopRecording] stop failed and was surfaced to the user');
                    trackTauriStopFailedSurfaced({
                      sessionId,
                      path: 'rust',
                      attempts: STOP_SESSION_MAX_ATTEMPTS,
                    });
                    dispatch.timerModel.onStart(seconds);
                    alert(STOP_FAILED_ALERT);
                    // Persist the failed-session flag before clearing the hand-off,
                    // because throwing skips the normal unhealthy-stop marker below.
                    markSessionHadWebsocketError(sessionId);
                    // Fail the pause hand-off closed so a later End-session does not
                    // await an unsettled promise forever.
                    clearRustChunkUploadPauseFinalization(sessionId);
                    // Capture never stopped, so no finalize ran.
                    cancelNativeRecordingFinalizeAttempt(finalizeAttempt);
                    throw failDesktopStopAttempt(
                      input?.tracking,
                      'Desktop recording stop failed after retries'
                    );
                  }
                  // Recorder reports dead: use the unhealthy-stop recovery instead of a
                  // false still-recording failure. A pause here is still a failed attempt.
                  captureStopUnhealthy = true;
                  logger.warn(
                    '[StopRecording] stop IPCs failed but recorder reports not recording; continuing as unhealthy stop',
                    { sessionId }
                  );
                }
              }
            }
            releaseFencedStarts?.();
            // The recorder is stopped, or confirmed dead after failed stop IPCs.
            // The finalize ended here, whatever the teardown below takes, and a
            // throw in it must not report a finished stop as abandoned.
            const finalizeEndTime = performance.now();
            let finalizeClosed = false;
            const closeFinalize = (facts: StopOnlineChunkUploadResult | undefined) => {
              if (finalizeClosed) return;
              finalizeClosed = true;
              completeNativeRecordingFinalizeAttempt(
                finalizeAttempt,
                nativeFinalizeFacts(facts, Boolean(input?.isPaused)),
                finalizeEndTime
              );
            };
            if (stopResult !== undefined) closeFinalize(stopResult);
            let recovered: NativeTerminalOutcome | undefined;
            try {
              if (input?.isPaused) {
                finishPauseAtCaptureStop(input?.tracking, captureStopUnhealthy);
              } else {
                finishStopAtCaptureStop({
                  tracking: input?.tracking,
                  timerSeconds: seconds,
                  rustDurationMs: stopResult?.durationMs,
                  detachedBackupMux,
                });
              }
              detachOnlineChunkUploadListeners();

              // The recorder is down: drop silent-capture state and warning surfaces.
              // Done after the stop so an aborted stop does not wipe a live recording's warnings.
              dispatch.tauriRecordModel.resetCaptureSilence();
              setPreStartSystemMuteUnhealthy(false);
              setPreStartSystemMutePopoverOpen(false);

              // Persist `session_duration` with the upload flush. Awaited so the PATCH
              // settles before note generation (the backend reads the server duration).
              // It never rejects, and runs on the failure branch too.
              await persistAuthoritativeDuration(stopResult?.durationMs);

              // `backupConfirmed` below only skips a redundant backup re-upload; it is
              // not part of the note-gen gate.
              // A timeout is not an outcome: ask native what happened before deciding.
              if (stopThrew && selfHealingEnabled) {
                recovered = await recoverTypedOutcome();
                if (recovered) {
                  logger.info(
                    '[StopRecording] recovered the typed outcome after a stop timeout',
                    {
                      sessionId,
                      reason: recovered.reason,
                      classification: recovered.classification,
                      failedChunks: recovered.failedChunks,
                      finishSucceeded: recovered.finishSucceeded,
                    }
                  );
                  // A forced termination or segment stop that landed while we waited is
                  // a real outcome but never permission to generate. Keep `stopThrew`.
                  if (outcomeMayGenerateNote(recovered.reason)) {
                    stopResult = terminalOutcomeAsStopResult(recovered);
                    stopThrew = false;
                  }
                }
              }
            } finally {
              // A stop that never answered closes on the recovered outcome, even
              // when the note-generation gate withholds it, or as unanswered
              // when the teardown threw first.
              closeFinalize(
                recovered ? terminalOutcomeAsStopResult(recovered) : undefined
              );
            }
            rememberClientRustStop(
              sessionId,
              rustStopClaimSignal(stopResult, sentReason)
            );
            // `backupPendingAsync` counts here because a detached mux moved the WAVs,
            // so a re-extract would upload a near-empty MP3. Read what Rust reports.
            const backupFacts = stopResult ?? lateRestartStop;
            const backupConfirmed =
              !stopThrew &&
              (backupFacts?.backupPendingAsync === true ||
                backupFacts?.backupUploaded === true) &&
              backupFacts?.finishSucceeded === true;
            // Separate from `backupConfirmed` (which also needs `:finish`, so an offline
            // stop would re-extract from renamed paths). This only asks whether the live
            // WAV paths still hold the audio, using Rust's `backupDeferred` answer.
            const sourcesVacated = backupFacts?.backupDeferred === true;
            const pauseFinalizationOutcome = getRustChunkUploadPauseFinalizationOutcome({
              stopResult,
              stopThrew,
              selfHealingEnabled,
              failedChunkThreshold,
            });
            // The cadence Rust is running, locked at start (the live payload can differ).
            // Check the owner: another session's start may have overwritten it.
            const lockedChunkIntervalSecs =
              lockedChunkInterval?.sessionId === sessionId
                ? lockedChunkInterval.chunkIntervalSecs
                : undefined;
            if (
              isDesktopRecordingLivenessEnabled() &&
              lockedChunkIntervalSecs === undefined
            ) {
              // Debug, not warn: the skip is expected. Logged so support can tell
              // "heuristic cleared this session" from "heuristic never ran", and why.
              logger.debug('DESKTOP_RECORDING_CHUNK_HEURISTIC_SKIPPED', {
                sessionId,
                reason:
                  lockedChunkInterval === undefined
                    ? 'no_locked_chunk_cadence'
                    : 'chunk_cadence_owned_by_other_session',
                lockedBySessionId: lockedChunkInterval?.sessionId,
              });
            }
            const handedOverLocalSessionId = handedOverLocalSessionIdOf(
              stopResult,
              Boolean(input?.isPaused)
            );
            const captureTruncatedByChunks =
              isDesktopRecordingLivenessEnabled() &&
              // `totalChunks` counts uploaded chunks only. A handover accounts for
              // the missing chunks (they are in the offline queue).
              !handedOverLocalSessionId &&
              !stopThrew &&
              stopResult !== undefined &&
              // No locked cadence (reload or non-Rust path): skip rather than use a
              // default, since a false positive wrongly blocks note generation.
              lockedChunkIntervalSecs !== undefined &&
              // `totalChunks` spans the whole session, so this can miss a truncation
              // after a healthy first segment. Missing it is safer than blocking.
              isCaptureTruncatedVsWallClock({
                wallClockSecs: seconds,
                segmentDurationMs: stopResult.durationMs,
                totalChunks: stopResult.totalChunks,
                chunkIntervalSecs: lockedChunkIntervalSecs,
              });
            if (captureTruncatedByChunks && stopResult) {
              logger.warn(
                '[StopRecording] Capture truncated vs wall-clock session time',
                {
                  sessionId,
                  wallClockSecs: seconds,
                  totalChunks: stopResult.totalChunks,
                  durationMs: stopResult.durationMs,
                  chunkIntervalSecs: lockedChunkIntervalSecs,
                }
              );
            }
            const captureTruncated =
              input?.captureTruncated === true || captureTruncatedByChunks;
            // Truncation and the failed-chunk threshold are independent gates on
            // `recordingSafe`; either can block note generation. Mirrors the legacy
            // path's `chunksHadIssues`.
            const recordingSafe =
              pauseFinalizationOutcome === 'healthy' &&
              !input?.wavWriterFailed &&
              !captureTruncated &&
              !silentCapture;
            // Stored server-side: narrower than `backupConfirmed`, which also counts an
            // in-flight `backupPendingAsync` upload. Only `backupUploaded` is acknowledged.
            // A reconcile reads its late-restart stop: audio Rust confirmed server-side
            // generates the note as the FE re-upload it replaces would have.
            const backupStoredServerSide =
              !stopThrew &&
              backupFacts?.backupUploaded === true &&
              backupFacts?.finishSucceeded === true;
            // Unhealthy stop where the audio already landed, so nothing else clears the
            // gate or generates the note. Truncation, quarantined WAV and silence are excluded.
            const backupSafeWithoutUpload =
              !recordingSafe &&
              backupStoredServerSide &&
              !captureTruncated &&
              !silentCapture &&
              !input?.wavWriterFailed;
            logger.info('DESKTOP_RECORDING_STOP_OUTCOME', {
              sessionId,
              path: 'rust-chunk-upload',
              recordingSafe,
              captureTruncated,
              backupSafeWithoutUpload,
              silentCapture: Boolean(silentCapture),
              // The input that decided which stop path ran (the rest is what the stop reported).
              detachedBackupMux,
              backupUploaded: stopResult?.backupUploaded,
              // Tells a granted detach apart from a lost backup that advanced anyway.
              backupPendingAsync: stopResult?.backupPendingAsync,
              // With `backupPendingAsync`, tells a healthy detached stop from a snapshot whose inline mux failed.
              backupDeferred: stopResult?.backupDeferred,
              backupRetained: stopResult?.backupRetained,
              finishSucceeded: stopResult?.finishSucceeded,
              totalChunks: stopResult?.totalChunks,
              failedChunks: stopResult?.failedChunks,
              offlineHandedOver: Boolean(handedOverLocalSessionId),
              // Uses the same constants as the branch below so telemetry matches behavior.
              advancingToNoteGen:
                (recordingSafe || backupSafeWithoutUpload) &&
                !input?.isPaused &&
                !handedOverLocalSessionId,
            });
            // A handover outranks a healthy verdict: generating offline would fail and
            // race the note the reconnect sync generates.
            if (recordingSafe && !handedOverLocalSessionId) {
              pauseFinalization?.resolve(pauseFinalizationOutcome);
              void recorderFmc.cleanup_recordings();
              if (isOnPrem) {
                // Drop the in-progress gate so status=complete can run after :finish.
                // Note-gen will re-seed progress while waiting on any remaining work.
                setSessionChunkUploadProgress(sessionId, undefined);
                void invalidateLiveTranscript(sessionId);
              }
              if (shouldUseDocumentFlow) {
                createEmptyDocumentFromNote({
                  trigger: 'autonomous',
                  generationMethod: GenerationMethod.TEMPLATE,
                  templateCategory: template.template_category,
                  templateId: template.id,
                });
                completeArtifactGeneration({ kind: 'note', sessionId });
              } else if (!input?.isPaused) {
                dispatch.scribeRecordModel._actionPostRecording({ sessionID: sessionId });
              }
            } else {
              // `onSuccess` reads `stopUnhealthy` to force-complete artifact generation,
              // so a stop that hands off to note generation must not set it.
              stopUnhealthy = !backupSafeWithoutUpload;
              // Failure: keep the WAVs, flag the session and show the upload-required
              // modal. Re-upload from the WAVs only if the backup is not already confirmed.
              const stopOutcomeContext = {
                sessionId,
                stopThrew,
                backupUploaded: stopResult?.backupUploaded,
                // Says whether the audio is in flight (detached) or gone.
                backupPendingAsync: stopResult?.backupPendingAsync,
                finishSucceeded: stopResult?.finishSucceeded,
                totalChunks: stopResult?.totalChunks,
                failedChunks: stopResult?.failedChunks,
                offlineHandedOver: Boolean(handedOverLocalSessionId),
              };
              // A handover is not a surfaced failure (audio is queued offline), so it
              // logs at info rather than the warn an upload-required stop alerts on.
              if (handedOverLocalSessionId) {
                logger.info(
                  '[StopRecording] Rust stop handed the unsent audio to the offline queue',
                  stopOutcomeContext
                );
              } else {
                logger.warn(
                  '[StopRecording] Rust stop not fully healthy; keeping WAVs and surfacing failure',
                  stopOutcomeContext
                );
              }
              // Skipped on a handover: the flag is a server write that fails offline.
              if (!handedOverLocalSessionId) {
                markSessionHadWebsocketError(sessionId);
              }
              if (silentCapture) {
                // Nothing worth recovering: the audio is digital zeros. No
                // backup staging, no upload, just the mic-named prompt.
                openSilentCaptureRecovery(silentCapture.micLabel);
                pauseFinalization?.resolve(pauseFinalizationOutcome);
                releaseUploadingWhenUnowned(input);
              } else if (captureTruncated) {
                openCaptureTruncatedRecovery();
                pauseFinalization?.resolve(pauseFinalizationOutcome);
                releaseUploadingWhenUnowned(input);
                await uploadTruncatedBackup(
                  backupConfirmed ||
                    backupFacts?.backupRetained === true ||
                    sourcesVacated ||
                    nativeStopStillRunning ||
                    input?.wavWriterFailed === true,
                  input?.isPaused
                );
              } else if (handedOverLocalSessionId) {
                // The reconnect sync will upload the queued audio and generate the note,
                // so clear the gate and skip the re-upload. The retained backup is a safety net.
                setUploadRequiredModal((current) =>
                  current === sessionId ? null : current
                );
                pauseFinalization?.resolve(pauseFinalizationOutcome);
                releaseUploadingWhenUnowned(input);
                settleOfflineHandover({
                  sessionId,
                  localSessionId: handedOverLocalSessionId,
                  args: offlineHandover,
                });
                // The WAVs are kept, as below: chunks did fail.
              } else if (backupSafeWithoutUpload) {
                // Nothing is pending, so clear the gate (session-scoped, so another
                // session's modal stays up) and let the note go ahead.
                setUploadRequiredModal((current) =>
                  current === sessionId ? null : current
                );
                pauseFinalization?.resolve(pauseFinalizationOutcome);
                if (isOnPrem) {
                  // Same as the healthy branch: the in-progress gate blocks status=complete.
                  setSessionChunkUploadProgress(sessionId, undefined);
                  void invalidateLiveTranscript(sessionId);
                }
                if (shouldUseDocumentFlow) {
                  createEmptyDocumentFromNote({
                    trigger: 'autonomous',
                    generationMethod: GenerationMethod.TEMPLATE,
                    templateCategory: template.template_category,
                    templateId: template.id,
                  });
                  completeArtifactGeneration({ kind: 'note', sessionId });
                } else if (!input?.isPaused) {
                  dispatch.scribeRecordModel._actionPostRecording({
                    sessionID: sessionId,
                  });
                } else {
                  // A pause never generates, so no one downstream owns the
                  // optimistic UPLOADING (a no-op for a pause, which never set it).
                  releaseUploadingWhenUnowned(input);
                }
                // WAVs are kept: chunks failed, so a manual re-upload must stay possible.
              } else {
                if (input?.wavWriterFailed) {
                  // No backup upload runs for a quarantined WAV, so set a terminal
                  // status before opening the modal or it sits on a stuck progress
                  // bar instead of the recovery actions.
                  settleWavWriterFailure();
                }
                setUploadRequiredModal(sessionId);
                // A native backup upload may still run, so mark it raised (a compare-and-set,
                // since a settle may have landed already); `useSelfHealingStopEvents` settles it.
                if (backupFacts?.backupPendingAsync === true) {
                  setNativeBackupUploadGate((gate) =>
                    raiseNativeBackupUploadGate(gate, sessionId)
                  );
                }
                pauseFinalization?.resolve(pauseFinalizationOutcome);
                if (
                  backupConfirmed ||
                  sourcesVacated ||
                  nativeStopStillRunning ||
                  input?.wavWriterFailed
                ) {
                  // No backup upload runs, so nothing downstream owns the status.
                  releaseUploadingWhenUnowned(input);
                }
                if (nativeStopStillRunning && !backupConfirmed) {
                  // Re-extracting now would race the native stop on the same WAVs.
                  logger.warn(
                    '[StopRecording] skipped the backup re-extract: a native stop still owns the WAVs',
                    { sessionId }
                  );
                }
                if (sourcesVacated && !backupConfirmed && !input?.wavWriterFailed) {
                  // No FE recovery attempt here (the source WAVs were moved), so log it.
                  // Known gap: a declined detach whose inline mux failed writes no
                  // recovery row, so a later storage sweep can delete the snapshot.
                  logger.warn(
                    '[StopRecording] skipped the backup re-extract: Rust moved the source WAVs off the live paths',
                    {
                      sessionId,
                      backupPendingAsync: backupFacts?.backupPendingAsync,
                      backupUploaded: backupFacts?.backupUploaded,
                      backupRetained: backupFacts?.backupRetained,
                      finishSucceeded: backupFacts?.finishSucceeded,
                    }
                  );
                }
                if (
                  !backupConfirmed &&
                  !sourcesVacated &&
                  !nativeStopStillRunning &&
                  !input?.wavWriterFailed
                ) {
                  // Recover from the lossless source WAVs (do NOT clean them up).
                  // The proven FE backup path re-extracts + uploads with its own retry.
                  try {
                    const res = await dispatch.tauriRecordModel.get_backup_file();
                    if (res?.blob && res.path && res.blob.size > 0) {
                      dispatch.tauriRecordModel.setBackupFile({
                        blob: res.blob,
                        path: res.path,
                      });
                      // Rust kept a recovery row for this recording, so settle it on success.
                      const settleRecordingId = stopResult?.backupRetained
                        ? stopResult.recordingId
                        : undefined;
                      if (stopResult?.backupRetained && !settleRecordingId) {
                        logger.warn(
                          '[StopRecording] a retained backup has no recording id, so its row will not settle',
                          { sessionId }
                        );
                      }
                      uploadBackupRecording({
                        sessionId: sessionId,
                        rawAudio: res.blob,
                        audioId: getUniqueId(),
                        mimeType: 'audio/mp3',
                        // A pause never generates a note, and `onSuccess` only generates when
                        // `!inBackground`, so a paused stop backgrounds this upload.
                        inBackground: Boolean(input?.isPaused),
                        // Says the consultation is over, which releases the app-quit close
                        // guard. A pause leaves it false so the guard stays armed.
                        sessionRecordingEnded: !input?.isPaused,
                        path: res.path,
                        recordingId: settleRecordingId,
                      });
                    } else {
                      // Nothing to upload, so no one downstream will release it.
                      releaseUploadingWhenUnowned(input);
                    }
                  } catch (error) {
                    logger.error(
                      '[StopRecording] backup recovery failed; WAVs retained for manual upload:',
                      error
                    );
                    releaseUploadingWhenUnowned(input);
                  }
                }
              }
            }

            // Reap the watch on every path that did not raise. A `raised` entry stays:
            // its upload outlives this stop and its own settle removes it.
            setNativeBackupUploadGate((gate) =>
              closeNativeBackupUploadWatch(gate, sessionId)
            );
            void recordingLifecycle.emitStopped(sessionId);
            return { stopUnhealthy, detachedBackupMux };
          }

          // ── Legacy (non-Rust) chunk-upload path ──
          // IMPORTANT: Await stopTauriRecording to ensure recorderFmc.stop() completes
          // before any operations that might access audio resources
          if (isOnPrem) {
            // Same as the Rust branch: keep live transcript in_progress until the
            // final flush lands to avoid an early status=complete.
            setSessionChunkUploadProgress(sessionId, 0);
          }
          let legacySweepResult: StopOnlineChunkUploadResult | undefined;
          // A failed stopTauriRecording must not advance to note gen (empty chunks look healthy).
          let legacyStopUnhealthy = false;
          if (input?.reconcileAlreadyStopped) {
            // The switch flow already stopped and flushed, so only reconcile the timer
            // the failed restart left running, then run the finalization tail.
            logger.warn(
              '[StopRecording] Reconciling an already-stopped recorder; skipping recorder stop',
              { sessionId }
            );
            dispatch.timerModel.onPause();
          } else {
            try {
              await pTimeout(dispatch.tauriRecordModel.stopTauriRecording(), 15000);
              logger.info('[StopRecording] Stop completed successfully', { sessionId });
            } catch (stopError) {
              logger.error('[StopRecording] stopTauriRecording IPC failed or timed out', {
                error: sanitizeError(stopError),
              });
              const interval = store.getState().tauriRecordModel.interval;
              if (interval) {
                clearInterval(interval);
              }
              dispatch.timerModel.onPause();
              if (await isRecorderStillRecording()) {
                logger
                  .child({ sessionId, path: 'legacy', attempts: 1 })
                  .error('[StopRecording] stop failed and was surfaced to the user');
                trackTauriStopFailedSurfaced({
                  sessionId,
                  path: 'legacy',
                  attempts: 1,
                });
                dispatch.timerModel.onStart(seconds);
                alert(STOP_FAILED_ALERT);
                throw failDesktopStopAttempt(
                  input?.tracking,
                  'Desktop recording stop failed'
                );
              }
              // Capture is dead but the stop IPC failed: like the Rust unhealthy branch,
              // keep WAVs and surface recovery rather than treating it as healthy.
              legacyStopUnhealthy = true;
              captureStopUnhealthy = true;
              logger.warn(
                '[StopRecording] stopTauriRecording failed but recorder reports not recording; continuing as unhealthy stop',
                { sessionId }
              );
            }
            // A wiped locked flag (e.g. a webview reload mid-session) can route
            // the stop here while the Rust upload engine owns a live session.
            try {
              legacySweepResult = await pTimeout(
                onlineChunkUploadBridge.stopSession(),
                LEGACY_SWEEP_TIMEOUT_MS
              );
              logger.warn(
                '[StopRecording] legacy stop found and stopped a live Rust upload session',
                {
                  sessionId,
                  backupUploaded: legacySweepResult.backupUploaded,
                  backupPendingAsync: legacySweepResult.backupPendingAsync,
                  finishSucceeded: legacySweepResult.finishSucceeded,
                  totalChunks: legacySweepResult.totalChunks,
                  failedChunks: legacySweepResult.failedChunks,
                }
              );
              // The sweep sends no reason; Rust records it as `userStop`.
              rememberClientRustStop(
                sessionId,
                rustStopClaimSignal(legacySweepResult, 'userStop')
              );
            } catch {
              // Expected on the legacy path: no Rust upload session is active.
            }
          }
          // The recorder is stopped, or confirmed dead after a failed stop IPC.
          if (input?.isPaused) {
            finishPauseAtCaptureStop(input?.tracking, captureStopUnhealthy);
          } else {
            finishStopAtCaptureStop({
              tracking: input?.tracking,
              timerSeconds: seconds,
              detachedBackupMux,
            });
          }

          // Recorder is down: drop silent-capture state and warning surfaces. Done after
          // the stop so an aborted stop does not wipe a live recording's state.
          dispatch.tauriRecordModel.resetCaptureSilence();
          setPreStartSystemMuteUnhealthy(false);
          setPreStartSystemMutePopoverOpen(false);

          // Skip the final chunk flush after a failed stop IPC (it causes 403 spam);
          // backup recovery below is the recovery path.
          if (!input?.wavWriterFailed && !legacyStopUnhealthy) {
            try {
              await getChunkRecordingAndUpload({ sessionId, intent: 'final' });
            } catch (error) {
              logger.error(
                'Failed to upload chunk recording during stop, continuing with stop flow:',
                error
              );
            }
          }

          // Duration persistence for the legacy path (no Rust durationMs); awaited
          // so it settles before note generation.
          await persistAuthoritativeDuration();

          if (getEnvironment() === ENV_NAME.DEV) {
            void dispatch.tauriRecordModel.getFilesForSession(sessionId);
          }
          const chunksIssues = chunks.blobs.filter(
            (chunk) => chunk.status === UploadStatus.Failed
          ).length;
          const allChunks = chunks.blobs.length;
          // Same flag-configurable tolerance as the Rust path (default 3).
          const legacyFailedChunkThreshold = resolveChunkFailureThreshold(
            selfHealingEnabled,
            failedChunkThreshold
          );
          // A swept Rust session owned the uploads (`chunks.blobs` is empty), so its
          // result gets the Rust path's health gate.
          const sweepUnhealthy =
            legacySweepResult !== undefined &&
            ((!selfHealingEnabled && legacySweepResult.backupUploaded !== true) ||
              legacySweepResult.finishSucceeded !== true ||
              legacySweepResult.failedChunks > legacyFailedChunkThreshold ||
              (legacySweepResult.totalChunks === 0 &&
                legacySweepResult.failedChunks > 0));
          // A failed stopTauriRecording counts as unhealthy even with empty
          // `chunks.blobs`. Fail closed: showing the upload modal beats generating a
          // note from an empty session, since the final flush may not have completed.
          const chunksHadIssues =
            input?.wavWriterFailed === true ||
            input?.captureTruncated === true ||
            silentCapture !== undefined ||
            legacyStopUnhealthy ||
            (allChunks !== 0 &&
              (allChunks === chunksIssues ||
                chunksIssues > legacyFailedChunkThreshold)) ||
            sweepUnhealthy;
          logger.info('DESKTOP_RECORDING_STOP_OUTCOME', {
            sessionId,
            path: 'legacy-fe-upload',
            totalChunks: allChunks,
            failedChunks: chunksIssues,
            legacyStopUnhealthy,
            sweptRustSession: legacySweepResult !== undefined,
            sweepUnhealthy,
            captureTruncated: input?.captureTruncated === true,
            silentCapture: Boolean(silentCapture),
            chunksHadIssues,
            advancingToNoteGen: !chunksHadIssues && !input?.isPaused,
          });
          if (chunksHadIssues) {
            stopUnhealthy = true;
            markSessionHadWebsocketError(sessionId);
            if (silentCapture) {
              openSilentCaptureRecovery(silentCapture.micLabel);
            } else if (input?.captureTruncated === true) {
              openCaptureTruncatedRecovery();
            } else if (input?.wavWriterFailed) {
              // The backup upload below is skipped for a quarantined WAV, so set a
              // terminal status first or the modal shows a stuck progress bar.
              settleWavWriterFailure();
              setUploadRequiredModal(sessionId);
            } else {
              setUploadRequiredModal(sessionId);
            }
          } else {
            if (isOnPrem) {
              // Flush landed cleanly: drop the in-progress gate so status=complete can run.
              setSessionChunkUploadProgress(sessionId, undefined);
              void invalidateLiveTranscript(sessionId);
            }
            if (shouldUseDocumentFlow) {
              createEmptyDocumentFromNote({
                trigger: 'autonomous',
                generationMethod: GenerationMethod.TEMPLATE,
                templateCategory: template.template_category,
                templateId: template.id,
              });
              completeArtifactGeneration({ kind: 'note', sessionId });
            } else {
              if (!input?.isPaused) {
                dispatch.scribeRecordModel._actionPostRecording({ sessionID: sessionId });
              }
            }
          }

          // Legacy FE backup path (the Rust path returns early and uploads its own backup).
          if (!input?.wavWriterFailed) {
            try {
              const res = await dispatch.tauriRecordModel.get_backup_file();
              if (!res) {
                logger.warn(
                  chunksHadIssues
                    ? 'No backup file retrieved; WAVs retained for manual upload'
                    : 'No backup file retrieved, but recording stopped successfully'
                );
                if (chunksHadIssues) releaseUploadingWhenUnowned(input);
                return { stopUnhealthy, detachedBackupMux };
              }
              const { blob, path } = res;
              if (blob && path) {
                // Keep local WAVs on an unhealthy stop so manual recovery stays possible.
                if (!chunksHadIssues) {
                  void recorderFmc.cleanup_recordings();
                }
                dispatch.tauriRecordModel.setBackupFile({ blob, path });
                if (blob.size > 0) {
                  uploadBackupRecording({
                    sessionId: sessionId,
                    rawAudio: blob,
                    audioId: getUniqueId(),
                    mimeType: 'audio/mp3',
                    // Foreground (and generate on success) only for an unhealthy final
                    // stop. A pause, truncation or all-zero capture backgrounds the
                    // backup and must not generate a note from partial or silent audio.
                    inBackground:
                      Boolean(input?.isPaused) ||
                      !chunksHadIssues ||
                      input?.captureTruncated === true ||
                      silentCapture !== undefined,
                    // A truncated or silent capture still ends the consultation; only a
                    // pause leaves the guard armed.
                    sessionRecordingEnded: !input?.isPaused,
                    generateNoteOnSuccess:
                      input?.captureTruncated || silentCapture ? false : undefined,
                    promptOnFailure: input?.captureTruncated === true,
                    partialAudio: input?.captureTruncated === true,
                    path: path,
                  });
                } else if (chunksHadIssues) {
                  releaseUploadingWhenUnowned(input);
                }
              } else if (chunksHadIssues) {
                releaseUploadingWhenUnowned(input);
              }
            } catch (error) {
              logger.error(
                chunksHadIssues
                  ? 'Failed to retrieve or upload backup file; WAVs retained for manual upload:'
                  : 'Failed to retrieve or upload backup file, but recording stopped successfully:',
                error
              );
              if (chunksHadIssues) releaseUploadingWhenUnowned(input);
            }
          }

          // Signal idle stream to restart after all recording cleanup is complete
          logger.debug(
            '[useMutateStopRecording] Recording cleanup complete, emitting stopped event'
          );
          // Emit event for decoupled listeners (DesktopIdleBars will handle restart)
          void recordingLifecycle.emitStopped(sessionId);
          logger.debug(
            '[useMutateStopRecording] Idle stream will auto-restart via event listener'
          );
          return { stopUnhealthy, detachedBackupMux };
        }
        // Web (non-Tauri) stops keep only the click-time duration PATCH (no
        // authoritative duration to flush here).
        logger.debug('session in stop recording: ', session?.session_id);

        // ownMediaRecorder?.stop();
        // socket?.close();
        // logger.debug

        // make sure it has started first to avoid too quick clicking
        if (USE_CHUNK_RECORDING()) {
          await waitUntil(() => {
            // Retrieve the latest session data from the cache
            const isRunning = store.getState().timerModel.isRunning;
            return Boolean(isRunning);
          }, 2000);
        }

        dispatch.scribeRecordModel._stopOwnRecording({ sessionID: sessionId });
        if (USE_CHUNK_RECORDING()) {
          chunkAudioRecorder?.stop(sessionId);
        }

        const chunksHadIssues = chunkAudioRecorder?.chunkHasIssues();

        const shouldShowUploadBackupModal =
          session &&
          (session?.isSocketError ||
            session?.had_ws_recording_failure ||
            chunksHadIssues);

        if (shouldShowUploadBackupModal) {
          stopUnhealthy = true;
          logger.debug('socket error in this session?: ' + session.isSocketError);
          logger.debug(
            'session had ws error previously: ' + session.had_ws_recording_failure
          );
          if (session?.isSocketError) {
            markSessionHadWebsocketError(session.session_id);
          }
          setUploadRequiredModal(sessionId);
        } else {
          if (shouldUseDocumentFlow) {
            createEmptyDocumentFromNote({
              trigger: 'autonomous',
              generationMethod: template.internal
                ? GenerationMethod.INTERNAL_TEMPLATE
                : GenerationMethod.TEMPLATE,
              templateCategory: template.template_category,
              templateId: template.id,
            });
            completeArtifactGeneration({ kind: 'note', sessionId });
          } else {
            if (!input?.isPaused) {
              dispatch.scribeRecordModel._actionPostRecording({ sessionID: sessionId });
            }
          }
        }

        const { blob, mimeType } = await stopOfflineRecordingAudio();
        // The backup recorder was the last live capture on this path.
        if (input?.isPaused) {
          finishPauseAtCaptureStop(input?.tracking, false);
        } else {
          finishStopAtCaptureStop({
            tracking: input?.tracking,
            timerSeconds: seconds,
            detachedBackupMux,
          });
        }

        const id = getUniqueId();
        uploadBackupRecording({
          sessionId: sessionId,
          audioId: id,
          rawAudio: blob,
          mimeType,
          inBackground: !shouldShowUploadBackupModal,
          sessionRecordingEnded: !input?.isPaused,
        });
        return { stopUnhealthy, detachedBackupMux };
      } finally {
        releaseFencedStarts?.();
        pauseFinalization?.resolve('unhealthy');
      }
    },
    onMutate: (input?: StopRecordingInput) => {
      // set session streaming status to uploading
      if (!input?.isPaused) {
        // A real stop ends the recording: release the transport stamp so the next
        // session routes by the input selector again.
        setActiveRecordingTransport(null);
        const previousSession = queryClient.getQueryData<QuerySessionResponse>([
          GET_SESSION_BY_ID,
          { sessionId },
        ]);
        beginArtifactGeneration({
          key: { kind: 'note', sessionId },
          outgoingContent: previousSession?.consult_note?.result ?? null,
          phase: 'uploading',
        });
        queryClient.setQueryData<QuerySessionResponse>(
          [GET_SESSION_BY_ID, { sessionId }],
          (oldData) => {
            if (!oldData) {
              return undefined;
            }

            const newData: QuerySessionResponse = {
              ...oldData,
              streamingStatus: StreamingStatus.UPLOADING,
            };
            return newData;
          }
        );
        return {
          previousStreamingStatus: previousSession?.streamingStatus,
          previousRecordingTransport: activeRecordingTransport,
        };
      }
      return undefined;
    },
    onSuccess: (data, input) => {
      if (!input?.isPaused && data?.stopUnhealthy) {
        // Upload-required modal, no note generated: release the note slot.
        completeArtifactGeneration({ kind: 'note', sessionId });
      }
      void queryClient.invalidateQueries({
        queryKey: [GET_TRANSCRIPT_BY_SESSION_ID, { sessionId }],
      });

      if (IS_LIVE_TRANSCRIPTS_ENABLED() && !isTablet && !isMobile) {
        mergeSessionWindows();
      }
    },
    onSettled: () => {
      // Belt-and-braces: catch any zombie recorder the teardown paths above
      // missed (the safety stop no-ops when the recorder is already down).
      if (isTauri()) {
        setTimeout(() => {
          void runRecorderSafetyStop(sessionId);
        }, RECORDER_SAFETY_STOP_DELAY_MS);
      }
    },
    onError: (error, input, context) => {
      // No-op unless the throw came before capture stopped.
      finishRecordingControlAttempt(input?.tracking, { outcome: 'failed', error });
      // Roll back the optimistic onMutate writes so a retry still routes at
      // the live recorder and the session does not read as finalizing.
      if (context) {
        queryClient.setQueryData<QuerySessionResponse>(
          [GET_SESSION_BY_ID, { sessionId }],
          (oldData) =>
            oldData
              ? { ...oldData, streamingStatus: context.previousStreamingStatus }
              : undefined
        );
        setActiveRecordingTransport(context.previousRecordingTransport);
        resetArtifactGeneration({ kind: 'note', sessionId });
      }
      logger.error('Error:', error);
      if (error instanceof AxiosError) {
        toast({
          tone: 'destructive',
          title: 'Failed to end recording',
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          description: get(error.response, 'data.detail.message', error.message),
        });
      }
    },
  });
}
